import { Alert, AlertIcon, Button, Flex, Text } from "@chakra-ui/react";
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useListSourcingEventFieldSourcesQuery,
  useReviewSourcingEventFieldSourcesMutation,
  type SourcingEventFieldSourceDto,
} from "../../../../../../autogen/bff-api";
import { useApiError } from "../../../../../../common/errors/useApiError";

interface FieldSources {
  fields: ReadonlyArray<SourcingEventFieldSourceDto>;
  canReview: boolean;
  confirm: (paths: string[]) => Promise<void>;
}

const FieldSourcesContext = createContext<FieldSources | null>(null);

export const FieldSourcesProvider = ({ eventId, children }: { eventId: string; children: ReactNode }) => {
  const { t } = useTranslation();
  const { data, isError, isFetching, refetch } = useListSourcingEventFieldSourcesQuery(
    { eventId },
    { refetchOnMountOrArgChange: true },
  );
  const [review] = useReviewSourcingEventFieldSourcesMutation();
  const { trigger } = useApiError();

  const confirm = useCallback(
    async (paths: string[]) => {
      try {
        await review({ eventId, reviewSourcingEventFieldSourcesRequest: { paths } }).unwrap();
      } catch (e) {
        trigger(e);
      }
    },
    [eventId, review, trigger],
  );

  const value = useMemo<FieldSources>(
    () => ({ fields: data?.fields ?? [], canReview: data?.canReview ?? false, confirm }),
    [data, confirm],
  );

  return (
    <FieldSourcesContext.Provider value={value}>
      {isError && (
        <Alert status="warning" borderRadius="lg" mt="6">
          <AlertIcon />
          <Flex flex="1" align="center" justify="space-between" gap="3" wrap="wrap">
            <Text fontSize="sm">
              {t("We could not fetch which fields the AI filled in. Fields the AI filled in are not marked.")}
            </Text>
            <Button size="sm" variant="outline" onClick={() => refetch()} isLoading={isFetching}>
              {t("Try again")}
            </Button>
          </Flex>
        </Alert>
      )}
      {children}
    </FieldSourcesContext.Provider>
  );
};

export const useAwaitingFieldSources = (paths: ReadonlyArray<string> | undefined, exact = false) => {
  const sources = useContext(FieldSourcesContext);
  const matches = exact ? (path: string, recorded: string) => path === recorded : sameOrNested;
  const awaiting =
    sources && paths?.length
      ? sources.fields.filter(
          (field) => field.awaitsReview && paths.some((path) => matches(path, field.path)),
        )
      : [];
  return {
    awaiting,
    canReview: sources?.canReview ?? false,
    confirm: async () => sources?.confirm(awaiting.map((field) => field.path)),
  };
};

const sameOrNested = (one: string, other: string) =>
  one === other || other.startsWith(`${one}.`) || one.startsWith(`${other}.`);
